import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from './base-api/api.service';
import {
  CreateProgressReport,
  ProgressReport,
  ProgressReportStatus,
  ReviewProgressReport,
} from '../models/progress-report';

/**
 * Academic progress reports — one series per award cycle, so every student
 * awarded for the current cycle reports on their progress.
 */
@Injectable({ providedIn: 'root' })
export class ProgressReportApiService {
  private readonly api = inject(ApiService);

  // ---- Student -----------------------------------------------------------

  getMyReports(): Observable<ProgressReport[]> {
    return this.api
      .get<{ data: ProgressReport[] }>('progress-reports/my-reports')
      .pipe(map((response) => response.data));
  }

  getReport(id: number): Observable<ProgressReport> {
    return this.api
      .get<{ data: ProgressReport }>(`progress-reports/${id}`)
      .pipe(map((response) => response.data));
  }

  submit(request: CreateProgressReport): Observable<ProgressReport> {
    return this.api
      .post<{ data: ProgressReport }>('progress-reports', request)
      .pipe(map((response) => response.data));
  }

  /** Uploads the academic record and returns the object key to attach. */
  uploadDocument(file: File): Observable<{ objectKey: string; fileName: string; size: number }> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.api
      .post<{ data: { objectKey: string; fileName: string; size: number } }>(
        'progress-reports/documents',
        formData
      )
      .pipe(map((response) => response.data));
  }

  downloadDocument(id: number): Observable<Blob> {
    return this.api.get<Blob>(`progress-reports/${id}/document`, { responseType: 'blob' });
  }

  // ---- Reviewer ----------------------------------------------------------

  getReportsForReview(
    cycle?: string | null,
    scholarshipId?: number | null,
    status?: ProgressReportStatus | null,
    search?: string
  ): Observable<ProgressReport[]> {
    const params = new URLSearchParams();
    if (cycle) {
      params.set('cycle', cycle);
    }
    if (scholarshipId) {
      params.set('scholarshipId', String(scholarshipId));
    }
    if (status) {
      params.set('status', status);
    }
    if (search) {
      params.set('search', search);
    }
    const query = params.toString();

    return this.api
      .get<{ data: ProgressReport[] }>(`admin/progress-reports${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }

  /** Approve, send back for resubmission, or reject. */
  review(id: number, request: ReviewProgressReport): Observable<ProgressReport> {
    return this.api
      .put<{ data: ProgressReport }>(`admin/progress-reports/${id}/review`, request)
      .pipe(map((response) => response.data));
  }

  downloadReviewDocument(id: number): Observable<Blob> {
    return this.api.get<Blob>(`admin/progress-reports/${id}/document`, { responseType: 'blob' });
  }
}
